import { useEffect, useRef, useState } from "preact/hooks";
import { Scope } from "../../components";
import { SYLLABLE_BPM, SYLLABLE_SEGMENT_MS, SYLLABLE_TOTAL_MS, SYLLABLES } from "../../content/drills";
import { CLARITY } from "../../engine/clarity";
import { createOnsetDetector, type MicSession } from "../../audio/mic";
import { Metronome } from "../../audio/metronome";
import { profile as profileSignal, recordPlosiveAccuracyPB } from "../../state/store";
import type { MicState } from "../useMic";

type RhythmProps = {
  onDone: (clarityBase: number, label: string) => void;
  mic: MicSession | null;
  micState: MicState;
  micThreshold: number;
};

export function Rhythm({ onDone, mic, micState, micThreshold }: RhythmProps) {
  const [elapsed, setElapsed] = useState(0);
  const [pulse, setPulse] = useState(false);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const beatWindowRef = useRef<{ open: boolean; closesAt: number }>({ open: false, closesAt: 0 });
  const soundOn = profileSignal.value.settings.sound.master && profileSignal.value.settings.sound.metronome;

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const e = Date.now() - start;
      setElapsed(e);
      if (e >= SYLLABLE_TOTAL_MS) {
        clearInterval(tick);
        const total = hits + misses;
        const accuracy = total > 0 ? Math.round((hits / total) * 100) : 0;
        if (mic && total > 0) recordPlosiveAccuracyPB(accuracy);
        const scaled = total > 0 ? Math.round((accuracy / 100) * CLARITY.plosive) : CLARITY.plosive;
        onDone(Math.max(CLARITY.plosive - 10, scaled), "Syllable Explosions");
      }
    }, 100);
    return () => clearInterval(tick);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!soundOn) {
      const beatMs = 60000 / SYLLABLE_BPM;
      const beat = setInterval(() => setPulse((p) => !p), beatMs);
      return () => clearInterval(beat);
    }
    const metronome = new Metronome({
      bpm: SYLLABLE_BPM,
      accentEvery: 4,
      onBeat: () => {
        setPulse((p) => !p);
        beatWindowRef.current = { open: true, closesAt: performance.now() + 130 };
      },
    });
    metronome.start();
    return () => metronome.stop();
    // eslint-disable-next-line
  }, [soundOn]);

  useEffect(() => {
    if (!mic || micState !== "ready") return;
    const detect = createOnsetDetector(micThreshold);
    let raf = 0;
    function poll() {
      const now = performance.now();
      const w = beatWindowRef.current;
      if (w.open && now > w.closesAt) {
        w.open = false;
        setMisses((m) => m + 1);
      }
      if (detect(mic!, now) && w.open) {
        w.open = false;
        setHits((h) => h + 1);
      }
      raf = requestAnimationFrame(poll);
    }
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [mic, micState, micThreshold]);

  const segIndex = Math.min(SYLLABLES.length - 1, Math.floor(elapsed / SYLLABLE_SEGMENT_MS));
  const s = SYLLABLES[segIndex]!;
  const secondsLeft = Math.max(0, Math.ceil((SYLLABLE_TOTAL_MS - elapsed) / 1000));
  const total = hits + misses;

  return (
    <div class="stage stage-rhythm">
      <p class="stage-kicker">{s.muscle}</p>
      <div class={"display stage-hero stage-beat" + (pulse ? " stage-pulse" : "")}>{s.pair}</div>
      {mic && micState === "ready" && <Scope getTimeDomain={() => mic.timeDomain()} width={260} height={56} />}
      <p class="mono stage-clock">{secondsLeft}</p>
      {mic && micState === "ready" && total > 0 && (
        <p class="mono stage-detail">
          {hits} / {total} on the beat
        </p>
      )}
    </div>
  );
}
